// Contributor-scoped upload session identity.
//
// One UUID per browser (localStorage). Every upload-related request stamps this via the
// X-Upload-Session-Id header. Backend records it on POST /packages, guards TUS writes on
// PATCH/POST/DELETE by comparing against the row's owner, and swaps ownership via
// POST /api/v1/upload/sessions/{uploadId}/claim when the user takes over from another device.
//
// localStorage is the right scope: two tabs in the same browser share IndexedDB and the
// existing in-memory queue already serialises them, so we want a single owner for the whole
// browser. Private browsing / cleared data will mint a new session id — that's the same as
// arriving from a different browser and will trigger the same takeover UX.

const STORAGE_KEY = "clipn-upload-session-id-v1";

let cachedSessionId = null;

const generateId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback: RFC 4122 v4 via getRandomValues for older browsers. tus-js-client already
  // requires crypto for its fingerprints so this path is defensive only.
  const buf = new Uint8Array(16);
  crypto.getRandomValues(buf);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex = Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const getUploadSessionId = () => {
  if (cachedSessionId) return cachedSessionId;
  try {
    const persisted = window.localStorage.getItem(STORAGE_KEY);
    if (persisted && persisted.length > 0) {
      cachedSessionId = persisted;
      return cachedSessionId;
    }
  } catch (err) {
    // Access to localStorage can throw in private-mode Safari with tight quotas — fall back
    // to an in-memory id. The user gets a fresh id on next reload, which will re-trigger the
    // takeover UX. Acceptable degradation.
    console.warn("[uploadSessionId] localStorage read failed", err?.message);
  }
  cachedSessionId = generateId();
  try {
    window.localStorage.setItem(STORAGE_KEY, cachedSessionId);
  } catch (err) {
    console.warn("[uploadSessionId] localStorage write failed", err?.message);
  }
  return cachedSessionId;
};

// Test-only reset. Not intended for production code paths.
export const _resetUploadSessionIdForTests = () => {
  cachedSessionId = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

// Sign-out teardown (2026-08-06 multi-tenancy leak fix). Without this, the
// same browser keeps the same sessionId across users: manager uploads →
// row.session_id = X; manager logs out → sessionId X stays in localStorage;
// admin logs in on same browser → their /sessions/active request sends
// X-Upload-Session-Id: X, backend's session_id-scoped path returns manager's
// upload, and admin sees it in In Progress as if it were their own. Clearing
// the id here forces a fresh UUID on the next call so the admin's session
// only matches uploads THEY explicitly claimed via /admin/uploads.
//
// Trade-off: a same-user logout+re-login also mints a new sessionId, so any
// in-flight upload the user was managing directly (not via IDB rehydration)
// surfaces as "on another device" via the cross-device flow. Small UX cost;
// correctness of cross-user isolation wins.
export const clearUploadSessionIdOnSignOut = () => {
  cachedSessionId = null;
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    console.warn("[uploadSessionId] localStorage clear failed", err?.message);
  }
};

export const UPLOAD_SESSION_HEADER = "X-Upload-Session-Id";
